// Hooks de React Query para o domínio CRM Lead (Story 11.2).
// - useCrmLeadsInfinite — listagem paginada por cursor (DR-032) + filtros.
// - useCrmLead — detalhe.
// - useCreateCrmLead / useUpdateCrmLead / useDiscardCrmLead.
//
// Convenção: queryKey ['crm-leads', 'list', filters] e ['crm-leads', 'detail', id].
// Após qualquer mutation: invalidateQueries({ queryKey: ['crm-leads'] }).

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import {
  createCrmLead,
  discardCrmLead,
  fetchCrmLead,
  fetchCrmLeads,
  updateCrmLead,
  type CreateCrmLeadResult,
  type CrmLeadDetailEntity,
  type CrmLeadEntity,
  type CrmLeadListParams,
  type CrmLeadListResponse,
} from '../lib/crm-leads-api';
import type { CreateCrmLeadDto, DiscardCrmLeadDto, UpdateCrmLeadDto } from '@control-center/shared';

const DEFAULT_LIMIT = 20;

export const crmLeadsKeys = {
  all: ['crm-leads'] as const,
  lists: () => [...crmLeadsKeys.all, 'list'] as const,
  list: (params: Omit<CrmLeadListParams, 'cursor'>) => [...crmLeadsKeys.lists(), params] as const,
  details: () => [...crmLeadsKeys.all, 'detail'] as const,
  detail: (id: string) => [...crmLeadsKeys.details(), id] as const,
};

export type CrmLeadsListFilters = Omit<CrmLeadListParams, 'cursor' | 'limit'>;

export function useCrmLeadsInfinite(filters: CrmLeadsListFilters = {}) {
  return useInfiniteQuery<
    CrmLeadListResponse,
    Error,
    InfiniteData<CrmLeadListResponse>,
    ReturnType<typeof crmLeadsKeys.list>,
    string | undefined
  >({
    queryKey: crmLeadsKeys.list({ ...filters, limit: DEFAULT_LIMIT }),
    queryFn: ({ pageParam, signal }) =>
      fetchCrmLeads({ ...filters, limit: DEFAULT_LIMIT, cursor: pageParam }, signal),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useCrmLead(id: string | undefined) {
  return useQuery<CrmLeadDetailEntity, Error>({
    queryKey: id ? crmLeadsKeys.detail(id) : ['crm-leads', 'detail', 'noop'],
    queryFn: ({ signal }) => fetchCrmLead(id as string, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateCrmLead() {
  const qc = useQueryClient();
  return useMutation<CreateCrmLeadResult, Error, CreateCrmLeadDto>({
    mutationFn: (payload) => createCrmLead(payload),
    onSuccess: ({ lead }) => {
      qc.invalidateQueries({ queryKey: crmLeadsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmLeadsKeys.detail(lead.id) });
    },
  });
}

export interface UpdateCrmLeadArgs {
  id: string;
  payload: UpdateCrmLeadDto;
}

export function useUpdateCrmLead() {
  const qc = useQueryClient();
  return useMutation<CrmLeadEntity, Error, UpdateCrmLeadArgs>({
    mutationFn: ({ id, payload }) => updateCrmLead(id, payload),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: crmLeadsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmLeadsKeys.detail(lead.id) });
    },
  });
}

export interface DiscardCrmLeadArgs {
  id: string;
  payload: DiscardCrmLeadDto;
}

export function useDiscardCrmLead() {
  const qc = useQueryClient();
  return useMutation<CrmLeadEntity, Error, DiscardCrmLeadArgs>({
    mutationFn: ({ id, payload }) => discardCrmLead(id, payload),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: crmLeadsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmLeadsKeys.detail(lead.id) });
    },
  });
}
